import { signal, computed, effect } from "@preact/signals-core";
import { productData } from "./product-store";

// 1. Raw State - populated by theme-settings-provider
export const themeSettings = signal<{
  enableBackInStockAlerts: boolean;
  klaviyoCompanyId: string;
  shopDomain: string;
  showNewsletterCheckbox: boolean;
  promoCodes: string[];
  enablePromoCodeModal: boolean;
} | null>(null);

// Debug: Log when theme settings change
effect(() => {
  console.log(
    "[extended-offer-store] themeSettings changed:",
    themeSettings.value
  );
});

// Debug: Log when productData changes
effect(() => {
  console.log("[extended-offer-store] productData changed:", {
    id: productData.value?.id,
    title: productData.value?.title,
    tags: productData.value?.tags,
  });
});

// 2. Computed State
export const isExtendedOfferEnabled = computed(() => {
  const settings = themeSettings.value;
  const product = productData.value;

  if (!settings || !product) {
    console.log(
      "[extended-offer-store] isExtendedOfferEnabled: false (no settings or product)"
    );
    return false;
  }

  // Check all conditions
  const hasKlaviyoId =
    settings.klaviyoCompanyId && settings.klaviyoCompanyId.trim() !== "";
  const featureEnabled = settings.enableBackInStockAlerts;
  const notExcluded = !product.tags?.includes("no_extended_offers");

  const result = hasKlaviyoId && featureEnabled && notExcluded;

  console.log("[extended-offer-store] isExtendedOfferEnabled:", {
    result,
    hasKlaviyoId,
    klaviyoId: settings.klaviyoCompanyId,
    featureEnabled,
    notExcluded,
    productTags: product.tags,
  });

  // Warn if Klaviyo ID is missing when feature is enabled
  if (featureEnabled && !hasKlaviyoId) {
    console.warn(
      "[extended-offer-store] ⚠️  Back in stock alerts are ENABLED but Klaviyo Company ID is MISSING. Please configure it in theme settings."
    );
  }

  return result;
});

// 3. Types
export interface BackInStockRequest {
  email: string;
  variantId: number;
  subscribeToNewsletter?: boolean;
}

export interface BackInStockResponse {
  success: boolean;
  message?: string;
}

// 4. Actions
export const submitBackInStockRequest = async (
  request: BackInStockRequest
): Promise<BackInStockResponse> => {
  const settings = themeSettings.value;

  if (!settings?.klaviyoCompanyId) {
    return {
      success: false,
      message: "Klaviyo Company ID is not configured.",
    };
  }

  const payload = {
    data: {
      type: "back-in-stock-subscription",
      attributes: {
        profile: {
          data: {
            type: "profile",
            attributes: {
              email: request.email,
            },
          },
        },
        channels: ["EMAIL"],
      },
      relationships: {
        variant: {
          data: {
            type: "catalog-variant",
            id: `$shopify:::$default:::${request.variantId}`,
          },
        },
      },
    },
  };

  try {
    const response = await fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${settings.klaviyoCompanyId}`,
      {
        method: "POST",
        headers: {
          accept: "application/vnd.api+json",
          revision: "2024-10-15",
          "content-type": "application/vnd.api+json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (response.status >= 200 && response.status < 300) {
      return { success: true };
    } else {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        message:
          errorData.message || "Failed to subscribe to back-in-stock alerts.",
      };
    }
  } catch (error) {
    console.error("Error submitting back-in-stock request:", error);
    return {
      success: false,
      message: "An error occurred. Please try again later.",
    };
  }
};
